package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

@Composable
fun AdminCard(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Column(
        modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(16.px)
            .border(1.px, LineStyle.Solid, Color.rgba(31, 36, 33, 0.05f))
            .padding(18.px)
            .margin(bottom = 14.px)
            .boxShadow(offsetY = 2.px, blurRadius = 8.px, color = Color.rgba(31, 36, 33, 0.04f))
            .attrsModifier { classes("qk-adm-card") },
    ) {
        content()
    }
}

@Composable
fun AdminTitle(text: String) {
    SpanText(
        text,
        Modifier
            .fontSize(17.px)
            .fontWeight(FontWeight.Bold)
            .color(Brand.Charcoal)
            .margin(bottom = 12.px),
    )
}

@Composable
fun AdminButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    danger: Boolean = false,
    secondary: Boolean = false,
    dark: Boolean = false,
    enabled: Boolean = true,
    iconSvg: String? = null,
) {
    val background =
        when {
            !enabled -> Color.rgba(31, 36, 33, 0.07f)
            danger -> Brand.Coral
            secondary -> Brand.Surface
            dark -> Brand.Charcoal
            else -> Brand.Emerald
        }
    val foreground =
        when {
            !enabled -> Color.rgba(31, 36, 33, 0.42f)
            secondary -> Brand.Charcoal
            else -> Brand.Surface
        }
    val iconColor =
        when {
            !enabled -> "rgba(31,36,33,.42)"
            secondary -> "#6B7280"
            else -> "#FFFFFF"
        }
    Row(
        modifier
            .backgroundColor(background)
            .then(
                if (secondary && enabled) {
                    Modifier.border(1.px, LineStyle.Solid, Brand.Divider)
                } else {
                    Modifier
                },
            )
            .borderRadius(11.px)
            .height(40.px)
            .padding(leftRight = 16.px)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .attrsModifier { classes("qk-press"); attr("role", "button") }
            .onClick { if (enabled) onClick() },
        horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        iconSvg?.let {
            Box(Modifier.margin(right = 7.px)) {
                InlineIcon(it, 15, iconColor)
            }
        }
        SpanText(
            text,
            Modifier
                .fontSize(14.px)
                .fontWeight(FontWeight.SemiBold)
                .color(foreground)
                .attrsModifier { classes("qk-nowrap") },
        )
    }
}

/** Small round icon-only action (edit / delete / move). */
@Composable
fun IconAction(
    iconSvg: String,
    label: String,
    onClick: () -> Unit,
    danger: Boolean = false,
) {
    Box(
        Modifier
            .size(32.px)
            .minWidth(32.px)
            .borderRadius(999.px)
            .backgroundColor(if (danger) Brand.CoralSoft else Brand.WarmBackground)
            .cursor(Cursor.Pointer)
            .margin(left = 6.px)
            .attrsModifier { classes("qk-press"); attr("role", "button"); attr("aria-label", label) }
            .onClick { it.stopPropagation(); onClick() },
        contentAlignment = Alignment.Center,
    ) {
        InlineIcon(iconSvg, 14, if (danger) "#E05B4C" else "#6B7280")
    }
}

/** One entry of the compact "⋮" actions dropdown. */
class MoreAction(
    val iconSvg: String,
    val label: String,
    val danger: Boolean = false,
    val onClick: () -> Unit,
)

/**
 * Kebab ("⋮") button that opens a dropdown with the row's actions. Used on
 * phones instead of the inline icon strip, where long names leave no room.
 */
@Composable
fun MoreActionsMenu(actions: List<MoreAction>) {
    var open by remember { mutableStateOf(false) }

    Box(Modifier.position(Position.Relative).margin(left = 6.px)) {
        Box(
            Modifier
                .size(32.px)
                .minWidth(32.px)
                .borderRadius(999.px)
                .backgroundColor(Brand.WarmBackground)
                .cursor(Cursor.Pointer)
                .attrsModifier {
                    classes("qk-press")
                    attr("role", "button")
                    attr("aria-haspopup", "menu")
                    attr("aria-expanded", "$open")
                    attr("aria-label", AdminT.moreActions)
                }
                .onClick { it.stopPropagation(); open = !open },
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(AdminIcons.MoreVertical, 15, "#6B7280")
        }

        if (open) {
            // Invisible backdrop: closes the menu on any outside click.
            Box(
                Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .zIndex(58)
                    .onClick { it.stopPropagation(); open = false },
            )
            Column(
                Modifier
                    .position(Position.Absolute)
                    .top(38.px)
                    .right(0.px)
                    .minWidth(196.px)
                    .backgroundColor(Brand.Surface)
                    .borderRadius(14.px)
                    .padding(5.px)
                    .zIndex(59)
                    .boxShadow(offsetY = 8.px, blurRadius = 24.px, color = Color.rgba(31, 36, 33, 0.22f))
                    .attrsModifier { classes("qk-fade-in"); attr("role", "menu") },
            ) {
                actions.forEach { action ->
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .borderRadius(10.px)
                            .padding(topBottom = 9.px, leftRight = 11.px)
                            .cursor(Cursor.Pointer)
                            .attrsModifier { classes("qk-press"); attr("role", "menuitem") }
                            .onClick {
                                it.stopPropagation()
                                open = false
                                action.onClick()
                            },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        InlineIcon(action.iconSvg, 15, if (action.danger) "#E05B4C" else "#6B7280")
                        SpanText(
                            action.label,
                            Modifier
                                .fontSize(13.px)
                                .fontWeight(FontWeight.Medium)
                                .color(if (action.danger) Brand.CoralText else Brand.Charcoal)
                                .margin(left = 9.px)
                                .attrsModifier { classes("qk-nowrap") },
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun AdminError(message: String?) {
    if (!message.isNullOrBlank()) {
        SpanText(
            message,
            Modifier.fontSize(13.px).color(Brand.Coral).margin(topBottom = 6.px),
        )
    }
}

@Composable
fun AdminHint(text: String) {
    SpanText(
        text,
        Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(topBottom = 4.px),
    )
}

@Composable
fun AdminToggle(
    checked: Boolean,
    onToggle: (Boolean) -> Unit,
) {
    Box(
        Modifier
            .width(44.px)
            .minWidth(44.px)
            .height(25.px)
            .backgroundColor(if (checked) Brand.Emerald else Brand.Divider)
            .borderRadius(999.px)
            .cursor(Cursor.Pointer)
            .attrsModifier { classes("qk-press"); attr("role", "switch"); attr("aria-checked", "$checked") }
            .onClick { onToggle(!checked) }
            .padding(2.px),
        contentAlignment = if (checked) Alignment.CenterEnd else Alignment.CenterStart,
    ) {
        Box(
            Modifier
                .size(21.px)
                .backgroundColor(Brand.Surface)
                .borderRadius(999.px)
                .boxShadow(offsetY = 1.px, blurRadius = 3.px, color = Color.rgba(0, 0, 0, 0.2f)),
        )
    }
}

/** Colored status badge (e.g. subscription state, roles, stop-list). */
@Composable
fun AdminBadge(
    text: String,
    tone: String = "neutral", // "success" | "danger" | "warning" | "neutral"
) {
    val (bg, fg) =
        when (tone) {
            "success" -> Brand.GreenSoft to Brand.Green
            "danger" -> Brand.CoralSoft to Brand.CoralText
            "warning" -> Color.rgba(232, 163, 61, 0.15f) to Brand.Amber
            else -> Brand.WarmBackground to Brand.TextSecondary
        }
    Box(
        Modifier
            .backgroundColor(bg)
            .borderRadius(999.px)
            .padding(topBottom = 3.px, leftRight = 10.px),
    ) {
        SpanText(
            text,
            Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold).color(fg).attrsModifier { classes("qk-nowrap") },
        )
    }
}

/** Friendly empty-state block with an icon. */
@Composable
fun AdminEmpty(iconSvg: String, title: String, hint: String) {
    Column(
        Modifier.fillMaxWidth().padding(topBottom = 28.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            Modifier
                .size(56.px)
                .borderRadius(999.px)
                .backgroundColor(Brand.WarmBackground),
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(iconSvg, 24, "#C9C3BA")
        }
        SpanText(
            title,
            Modifier.fontSize(15.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal).margin(top = 12.px),
        )
        SpanText(
            hint,
            Modifier.fontSize(13.px).color(Brand.TextSecondary).margin(top = 4.px)
                .styleModifier { property("text-align", "center") },
        )
    }
}

/** Styled file-picker: looks like a secondary button, hides the raw input on top. */
@Composable
fun UploadButton(
    label: String,
    enabled: Boolean = true,
    onFile: (File) -> Unit,
) {
    Box(Modifier.position(Position.Relative)) {
        Row(
            Modifier
                .backgroundColor(Brand.Surface)
                .border(1.px, LineStyle.Solid, Brand.Divider)
                .borderRadius(11.px)
                .height(38.px)
                .padding(leftRight = 14.px)
                .attrsModifier { classes("qk-press") },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(AdminIcons.ImagePlus, 15, "#6B7280")
            SpanText(
                label,
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .margin(left = 7.px)
                    .attrsModifier { classes("qk-nowrap") },
            )
        }
        if (enabled) {
            Input(
                InputType.File,
                attrs = {
                    classes("qk-adm-input-file")
                    attr("accept", "image/png,image/jpeg")
                    onChange { event ->
                        val input = event.target as? HTMLInputElement
                        input?.files?.item(0)?.let(onFile)
                        input?.value = ""
                    }
                },
            )
        }
    }
}

/** Simple client-side slug preview matching backend transliteration rules. */
fun slugPreview(name: String): String {
    val translit =
        mapOf(
            'а' to "a", 'б' to "b", 'в' to "v", 'г' to "h", 'ґ' to "g", 'д' to "d",
            'е' to "e", 'є' to "ie", 'ж' to "zh", 'з' to "z", 'и' to "y", 'і' to "i",
            'ї' to "i", 'й' to "i", 'к' to "k", 'л' to "l", 'м' to "m", 'н' to "n",
            'о' to "o", 'п' to "p", 'р' to "r", 'с' to "s", 'т' to "t", 'у' to "u",
            'ф' to "f", 'х' to "kh", 'ц' to "ts", 'ч' to "ch", 'ш' to "sh", 'щ' to "shch",
            'ь' to "", 'ю' to "iu", 'я' to "ia", 'ы' to "y", 'э' to "e", 'ё' to "e", 'ъ' to "",
        )
    return name.lowercase()
        .map { translit[it] ?: it.toString() }
        .joinToString("")
        .replace(Regex("[^a-z0-9]+"), "-")
        .trim('-')
        .take(30)
        .trim('-')
}
